import {
  loginResponseSchema,
  type AuthenticatedResponse,
  type LoginResponse,
  type MfaMethod,
} from '@growup/accounts-shared';
import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { acceptSession, post } from './api';
import { confirmWithPasskey, passkeysSupported, signInWithPasskey, wasCancelled } from './passkeys';
import { siteLink, usePortal } from './portal';
import { safeReturnPath, useSession } from './session';
import { Button, Card, ErrorNote, Field, NarrowPage, Notice, PasswordInput, TextInput } from './ui';
import { useRunner } from './useRunner';

type Step =
  | { kind: 'credentials' }
  | { kind: 'code'; challengeToken: string; methods: MfaMethod[]; recovery: boolean }
  | { kind: 'enroll' };

/**
 * Sign-in with the central Grow uP account, as on growup.gg: login or email
 * plus password, then the second step if the account has one. The session is
 * this host's own (the refresh cookie is per host). Creating an account,
 * resetting a password and setting up a second step live on the main site.
 */
export function SignIn() {
  const [params] = useSearchParams();
  const returnTo = safeReturnPath(params.get('voltar'), '/painel');
  const navigate = useNavigate();
  const { session } = useSession();
  const portal = usePortal();
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const { busy, error, run, fail } = useRunner();

  if (session) return <Navigate to={returnTo} replace />;

  const finish = (result: AuthenticatedResponse) => {
    acceptSession(result);
    navigate(returnTo, { replace: true });
  };

  const afterLogin = (result: LoginResponse) => {
    setCode('');
    if (result.status === 'authenticated') {
      finish(result);
    } else if (result.status === 'mfa_required') {
      setStep({
        kind: 'code',
        challengeToken: result.challengeToken,
        methods: result.methods,
        recovery: !result.methods.includes('totp') && !result.methods.includes('passkey'),
      });
    } else {
      setStep({ kind: 'enroll' });
    }
  };

  const withPasskey = (action: () => Promise<AuthenticatedResponse>) =>
    run(async () => {
      try {
        finish(await action());
      } catch (e) {
        if (!wasCancelled(e)) throw e;
      }
    });

  const signUp = siteLink(portal, '/conta/registar');
  const reset = siteLink(portal, '/conta/repor-password');

  return (
    <NarrowPage title="Entrar">
      {step.kind === 'credentials' ? (
        <Card>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!identifier.trim() || !password) {
                fail('Escreve o login (ou email) e a password.');
                return;
              }
              void run(async () =>
                afterLogin(
                  await post(
                    '/api/identity/sessions',
                    { identifier, password },
                    loginResponseSchema,
                  ),
                ),
              );
            }}
          >
            <p className="text-body text-gu-ink-muted">
              Entra com a tua conta Grow uP, a mesma do site e da loja.
            </p>
            <Field label="Login ou email">
              <TextInput
                autoComplete="username webauthn"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
              />
            </Field>
            <Field label="Password">
              <PasswordInput
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <ErrorNote message={error} />
            <Button tone="primary" type="submit" className="self-start" disabled={busy}>
              Entrar
            </Button>
            {passkeysSupported() ? (
              <Button
                className="self-start"
                disabled={busy}
                onClick={() => void withPasskey(signInWithPasskey)}
              >
                Entrar com passkey
              </Button>
            ) : null}
            <div className="flex flex-col gap-1 text-body text-gu-ink-muted">
              {reset ? (
                <a className="gu-link" href={reset}>
                  Esqueci-me da password
                </a>
              ) : null}
              {signUp ? (
                <span>
                  Ainda não tens conta?{' '}
                  <a className="gu-link" href={signUp}>
                    Cria uma no site da Grow uP
                  </a>
                </span>
              ) : null}
            </div>
          </form>
        </Card>
      ) : null}

      {step.kind === 'code' ? (
        <Card title="Segundo passo">
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () =>
                afterLogin(
                  await post(
                    step.recovery
                      ? '/api/identity/sessions/mfa/recovery'
                      : '/api/identity/sessions/mfa/totp',
                    { challengeToken: step.challengeToken, code: code.trim() },
                    loginResponseSchema,
                  ),
                ),
              );
            }}
          >
            {step.methods.includes('passkey') && passkeysSupported() ? (
              <Button
                className="self-start"
                tone={step.methods.includes('totp') ? 'default' : 'primary'}
                disabled={busy}
                onClick={() => void withPasskey(() => confirmWithPasskey(step.challengeToken))}
              >
                Usar a passkey
              </Button>
            ) : null}
            {step.methods.includes('totp') || step.recovery ? (
              <>
                <Field
                  label={step.recovery ? 'Código de recuperação' : 'Código da app autenticadora'}
                >
                  <TextInput
                    autoComplete="one-time-code"
                    inputMode={step.recovery ? 'text' : 'numeric'}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Field>
                <Button tone="primary" type="submit" className="self-start" disabled={busy}>
                  Confirmar
                </Button>
              </>
            ) : null}
            <ErrorNote message={error} />
            <button
              type="button"
              className="text-left text-body text-gu-ink-muted hover:text-gu-ink"
              onClick={() => {
                setCode('');
                setStep({ ...step, recovery: !step.recovery });
              }}
            >
              {step.recovery
                ? 'Voltar ao segundo passo normal'
                : 'Não tenho o telemóvel nem a passkey: usar um código de recuperação'}
            </button>
          </form>
        </Card>
      ) : null}

      {step.kind === 'enroll' ? (
        <Notice title="Falta o segundo passo">
          A tua conta é de staff e precisa de um segundo passo para entrar. Ativa-o primeiro no site
          da Grow uP{portal ? ` (${new URL(portal.siteUrl).host}/conta)` : ''} e volta a entrar
          aqui.
        </Notice>
      ) : null}
    </NarrowPage>
  );
}
